import ModalBank, {
  ModalBankButton,
} from '@/components/ui/custom/modals/ModalBank/ModalBank';
import useCourseEditing from '@/data/hook/useCourseEditing';
import useToggle from '@/data/hook/useToggle';
import {
  createActivity,
  pushActivityLesson,
} from '@/data/services/_deprecated/activityServices';
import { updateLesson } from '@/data/services/lessonServices';
import { isPulished } from '@/functions/handleCourseStatusIcon';
import Activity from '@/models/_deprecated/Activity';
import ActivityConfig from '@/models/ActivityConfig';
import CoursePath from '@/models/CoursePath';
import { ApiError } from '@/models/Errors';
import Lesson from '@/models/Lesson';
import { ScheduledLessonTypeEnum } from '@/models/ScheduledLesson';
import Observable from '@/utils/observers/ObserverPattern';
import alert from '@/utils/shared/alert';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import TooltipHandler from '@/components/ui/custom/TooltipHandler';
import MainButton from '@/components/ui/custom/buttons/MainButton';
import ListActivityItem from './ListActivityItem';
import { LoadingIcon } from '@/components/icons';
import { VersioningStatusEnum } from '@/enums/VersioningStatus';
import useInfiniteService from '@/data/hook/useInfiniteService';
import { activitiesQueryKeys } from '@/data/services/querykeys';
import { useState } from 'react';

interface ListActivitiesProps {
  lesson: Lesson;
  type?: ScheduledLessonTypeEnum;
  coursePath?: CoursePath;
  blockId?: number;
  updateLessons?(lessonId?: number, loading?: boolean): Promise<void>;
  klassId?: number;
  readOnly?: boolean;
}

export default function ListActivities({
  lesson,
  coursePath,
  blockId,
  updateLessons,
  type,
  klassId,
  readOnly: readOnlyProp,
}: ListActivitiesProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'activity',
  });

  const { isOpen, open, close } = useToggle();
  const readOnly = readOnlyProp;
  const { t: editRulesT } = useTranslation('translation', {
    keyPrefix: 'adminPage.editCourseRules',
  });

  const observer = new Observable<number>();

  const requestKey = klassId || coursePath?.slug;
  const { allowBigChanges } = useCourseEditing();
  const navigate = useNavigate();

  const [newLessonId, setNewLessonId] = useState<number>(lesson.id);

  const {
    results: activities,
    invalidate: updateActivities,
    isInitialLoading,
  } = useInfiniteService({
    enabled: !!newLessonId,
    ...activitiesQueryKeys.list({
      lessonId: newLessonId,
    })._ctx.infinity,
  });

  const updateLessonStatus = async (lesson: Lesson) => {
    const isExtraLesson = type !== ScheduledLessonTypeEnum.NORMAL && !!type;

    if (isPulished(lesson.status)) {
      const newLesson = await updateLesson(
        {
          lessonId: lesson.id,
          slugCourseName: isExtraLesson ? undefined : coursePath?.slug,
          blockId,
        },
        { status: isExtraLesson ? VersioningStatusEnum.VERSIONING : undefined },
      );
      await updateLessons?.(newLesson.id);
      return newLesson.id;
    }

    return lesson.id;
  };

  const onCreate = async ({ lessonId }: { lessonId: number }) => {
    lessonId = await updateLessonStatus(lesson);

    const activity = await createActivity(lessonId, {
      name: 'Nova Atividade',
      content: [],
      config: {} as ActivityConfig,
    });

    return { ...activity, lesson: lessonId };
  };

  const { mutate: pushActivity, isPending: pushingActivity } = useMutation(
    pushActivityLesson,
    {
      async onSuccess() {
        close();
        await updateLessons?.();
        await updateActivities();
      },
      onError(error: any) {
        const apiError = new ApiError(error);
        alert.error(apiError.getErrorMessage());
      },
    },
  );

  const onUseActivity = async (activity: Activity) => {
    const lessonId = await updateLessonStatus(lesson);

    if (lessonId) {
      const activityId = activity.id;
      pushActivity({ lessonId, activityId });
    }
  };

  const { mutate: create, isPending: isCreating } = useMutation(onCreate, {
    onSuccess: async createdActivity => {
      if (!coursePath && !klassId) {
        return navigate(
          `/admin/lessons/${createdActivity.lesson}/activities/${createdActivity.id}`,
        );
      }
      const isCourse = typeof requestKey === 'string';
      const adminUrl = `/admin/courses/${requestKey}/lessons/${createdActivity.lesson}/activities/${createdActivity.id}`;
      const extraLessonUrl = `/class/${requestKey}/lessons/${createdActivity.lesson}/activities/${createdActivity.id}`;
      const route = isCourse ? adminUrl : extraLessonUrl;
      await updateLessons?.();
      navigate(route);
    },
    onError: (err: any) => {
      const apiError = new ApiError(err);
      alert.error(apiError.getErrorMessage());
    },
  });

  return (
    <div className="grid gap-4">
      <ConditionalRenderer condition={isInitialLoading}>
        <LoadingIcon className="w-20 justify-self-center text-secondary/40" />
      </ConditionalRenderer>
      <ConditionalRenderer condition={activities?.length}>
        <ul className="grid gap-8">
          {activities?.map((activity, index) => {
            return (
              <ListActivityItem
                key={index}
                activity={activity}
                allowBigChanges={allowBigChanges}
                coursePath={coursePath}
                klassId={klassId}
                lesson={lesson}
                observer={observer}
                readOnly={readOnly}
                updateActivities={updateActivities}
                updateLessons={updateLessons}
                blockId={blockId}
                setNewLessonId={setNewLessonId}
              />
            );
          })}
        </ul>
      </ConditionalRenderer>
      <ConditionalRenderer condition={!readOnly}>
        <TooltipHandler
          renderTooltip={!allowBigChanges}
          className="w-fit"
          tooltipMessage={editRulesT('cantModify')}
        >
          <div className="flex flex-wrap gap-2">
            <MainButton
              dataTestId="newActivityButton"
              disabled={!allowBigChanges}
              loading={isCreating}
              onClick={() =>
                create({
                  lessonId: lesson.id,
                })
              }
              icon={<PlusIcon />}
              text={t('newActivity')}
            />
            <ModalBankButton
              onClick={open}
              mode="activities"
              disabled={!allowBigChanges}
            />
          </div>
        </TooltipHandler>
      </ConditionalRenderer>
      <ModalBank
        visible={isOpen}
        onClose={close}
        initial="activities"
        onUseActivity={onUseActivity}
        loading={pushingActivity}
      />
    </div>
  );
}
